import { FormEvent, ReactNode, useEffect, useRef, useState } from 'react'

import { Settings } from '../../api/settings'
import { useIsAccountClosed } from '../../components/AppShell'
import { ButtonAnchor, RutField, SubmitButton, TextField } from '../../components/common'
import styles from './SettingsPage.module.scss'

type FormValues = Record<string, string | number>

export type FormProps<T extends FormValues> = {
  settings: Settings
  onDirty: (save: () => Promise<boolean>) => void
  onClean: () => void
  onSubmit: (values: T) => Promise<void>
}

export function useSettingsForm<T extends FormValues>(initial: T, { onDirty, onClean, onSubmit }: Omit<FormProps<T>, 'settings'>) {
  const [values, setValues] = useState(initial)
  const [submitting, setSubmitting] = useState(false)
  const formRef = useRef<HTMLFormElement>(null)

  const save = async (): Promise<boolean> => {
    if (formRef.current && !formRef.current.reportValidity()) {
      return false
    }
    setSubmitting(true)
    try {
      await onSubmit(values)
      onClean()
      return true
    } catch {
      return false
    } finally {
      setSubmitting(false)
    }
  }

  useEffect(() => {
    const changed = Object.keys(initial).some((key) => values[key] !== initial[key])
    if (changed) {
      onDirty(save)
    } else {
      onClean()
    }
  }, [values])

  const set =
    <K extends keyof T>(field: K) =>
    (value: T[K]) =>
      setValues((current) => ({ ...current, [field]: value }))

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault()
    if (!submitting) {
      void save()
    }
  }

  return { values, set, submitting, handleSubmit, formRef }
}

export function UpdateButton({ submitting }: { submitting: boolean }) {
  const closed = useIsAccountClosed()
  if (closed) {
    return null
  }
  return <SubmitButton label="Actualizar" block disabled={submitting} />
}

export function SectionHeading({ children }: { children: ReactNode }) {
  return <h4 className="text-center blue-subtitle">{children}</h4>
}

export function ProfileForm({ settings, onDirty, onClean, onSubmit }: FormProps<Record<string, string>>) {
  const { professional, profession } = settings
  const { values, set, submitting, handleSubmit, formRef } = useSettingsForm(
    {
      rut: professional.rut ?? '',
      name: professional.name ?? '',
      phone: professional.phone ?? '',
      address: professional.address ?? '',
      commune: professional.commune ?? '',
      city: professional.city ?? '',
      icm: professional.icm ?? '',
      reg_sis: professional.reg_sis ?? '',
      profession_name: profession?.title ?? '',
    },
    { onDirty, onClean, onSubmit },
  )

  return (
    <form ref={formRef} onSubmit={handleSubmit}>
      <h3 className="text-center blue-subtitle">Tus datos</h3>
      <div className="col-md-12 row">
        <SectionHeading>Información Personal</SectionHeading>
        <RutField className="col-md-6" label="Rut" value={values.rut} onChange={set('rut')} />
        <TextField className="col-md-6" label="Nombre" value={values.name} onChange={set('name')} />
        <div className="col-md-6">
          <TextField
            label="Email"
            type="email"
            value={professional.email}
            readOnly
            onChange={() => {}}
            hint="Para cambiar tu correo, escríbenos a contacto@historiaclinica.cl"
          />
        </div>
        <TextField className="col-md-6" label="Teléfono" value={values.phone} onChange={set('phone')} />
        <TextField className="col-md-6" label="Dirección" value={values.address} onChange={set('address')} />
        <TextField className="col-md-3" label="Comuna" value={values.commune} onChange={set('commune')} />
        <TextField className="col-md-3" label="Ciudad" value={values.city} onChange={set('city')} />

        <SectionHeading>Información Profesional</SectionHeading>
        {profession?.can_prescript_recipes && (
          <>
            <TextField className="col-md-6" label="I.C.M" value={values.icm} onChange={set('icm')} />
            <TextField className="col-md-6" label="REG-SIS Nº" value={values.reg_sis} onChange={set('reg_sis')} />
          </>
        )}
        {profession && (
          <div className="col-md-12">
            <TextField
              className={`col-md-6 ${styles.professionField}`}
              label="Profesión"
              value={values.profession_name}
              onChange={set('profession_name')}
            />
          </div>
        )}
        <div className="col-md-12">
          <UpdateButton submitting={submitting} />
        </div>
      </div>
    </form>
  )
}

// A closed account may still save the billing details a payment asks for, and nothing else
const BILLING_INFO_FIELDS = ['payment_rut', 'payment_address', 'payment_cmna'] as const

export function HealthCenterForm({ settings, onDirty, onClean, onSubmit }: FormProps<Record<string, string>>) {
  const { health_center, paths } = settings
  const closed = useIsAccountClosed()
  const submitBillingInfo = (values: Record<string, string>) =>
    onSubmit(Object.fromEntries(BILLING_INFO_FIELDS.map((field) => [field, values[field] ?? ''])))
  const { values, set, submitting, handleSubmit, formRef } = useSettingsForm(
    {
      name: health_center.name ?? '',
      rut: health_center.rut ?? '',
      phone: health_center.phone ?? '',
      address: health_center.address ?? '',
      commune: health_center.commune ?? '',
      city: health_center.city ?? '',
      payment_rut: health_center.payment_rut ?? '',
      payment_address: health_center.payment_address ?? '',
      payment_cmna: health_center.payment_cmna ?? '',
    },
    { onDirty, onClean, onSubmit: closed ? submitBillingInfo : onSubmit },
  )

  return (
    <form ref={formRef} onSubmit={handleSubmit}>
      <div className="row">
        <h3 className="text-center blue-subtitle">Información del Centro</h3>
        <SectionHeading>General</SectionHeading>
        <div className="col-md-12">
          <TextField className="col-sm-5" label="Nombre" autoComplete="off" value={values.name} onChange={set('name')} readOnly={closed} />
          <RutField className="col-sm-3" label="Rut" autoComplete="off" value={values.rut} onChange={set('rut')} readOnly={closed} />
          <TextField className="col-sm-4" label="Teléfono" autoComplete="off" value={values.phone} onChange={set('phone')} readOnly={closed} />
        </div>
        <div className="col-md-12">
          <TextField className="col-sm-6" label="Dirección" autoComplete="off" value={values.address} onChange={set('address')} readOnly={closed} />
          <TextField className="col-sm-3" label="Comuna" autoComplete="off" value={values.commune} onChange={set('commune')} readOnly={closed} />
          <TextField className="col-sm-3" label="Ciudad" autoComplete="off" value={values.city} onChange={set('city')} readOnly={closed} />
        </div>
        <div className="col-xs-12">
          <SectionHeading>Facturación</SectionHeading>
          <RutField
            className="col-sm-6"
            label="Rut de facturación"
            autoComplete="off"
            value={values.payment_rut}
            onChange={set('payment_rut')}
          />
          <TextField
            className="col-sm-6"
            label="Dirección de facturación"
            autoComplete="off"
            value={values.payment_address}
            onChange={set('payment_address')}
          />
          <TextField
            className="col-sm-6"
            label="Comuna de facturación"
            autoComplete="off"
            value={values.payment_cmna}
            onChange={set('payment_cmna')}
          />
        </div>
        <div className="col-xs-12">
          <SectionHeading>Profesionales</SectionHeading>
          <div className={`col-xs-12 ${styles.centered}`}>
            <ButtonAnchor href={paths.professionals} variant="secondary">
              Gestionar cuentas
            </ButtonAnchor>
          </div>
        </div>
        <div className="col-xs-12">
          <SubmitButton label="Actualizar" block disabled={submitting} />
        </div>
      </div>
    </form>
  )
}

export function PasswordForm({ onSubmit }: { onSubmit: (values: Record<string, string>) => Promise<void> }) {
  const [password, setPassword] = useState('')
  const [passwordConfirmation, setPasswordConfirmation] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault()
    if (submitting) {
      return
    }
    setSubmitting(true)
    try {
      await onSubmit({ password, password_confirmation: passwordConfirmation })
      setPassword('')
      setPasswordConfirmation('')
    } catch {
      // The page already shows the request's errors.
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <form onSubmit={(event) => void handleSubmit(event)}>
      <h3 className="text-center blue-subtitle">Cambiar Contraseña</h3>
      <div className="col-md-12">
        <TextField label="Contraseña" type="password" autoComplete="off" value={password} onChange={setPassword} />
        <TextField
          label="Confirme su nueva contraseña"
          type="password"
          value={passwordConfirmation}
          onChange={setPasswordConfirmation}
        />
      </div>
      <div className="col-md-12">
        <UpdateButton submitting={submitting} />
      </div>
    </form>
  )
}
